/* Progressive, contextual preference capture for Preworn browse surfaces. */
(function () {
  'use strict';

  var STORAGE_KEY = 'prewornPreferencePromptsV1';
  var SESSION_KEY = 'prewornPreferencePromptSessionV1';
  var COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function readStore(storage, key) {
    try {
      var value = JSON.parse(storage.getItem(key) || '{}');
      return value && typeof value === 'object' ? value : {};
    } catch (error) {
      return {};
    }
  }

  function writeStore(storage, key, value) {
    try { storage.setItem(key, JSON.stringify(value)); }
    catch (error) { /* Preference capture must never break browsing. */ }
  }

  function emit(name, properties) {
    var detail = properties || {};
    window.dispatchEvent(new CustomEvent('preworn:' + name, { detail: detail }));
    window.dispatchEvent(new CustomEvent('preworn:analytics', {
      detail: { event: name, properties: detail }
    }));
  }

  function formatCount(value) {
    value = Number(value);
    return isFinite(value) && value >= 0 ? value.toLocaleString('en-GB') : '';
  }

  function selectedValues(value) {
    var values = Array.isArray(value) ? value : (value ? [value] : []);
    return values.map(function (item) { return String(item || '').trim(); })
      .filter(function (item, index, items) {
        return item && items.indexOf(item) === index;
      });
  }

  function hangerIcon() {
    return '<svg viewBox="0 0 48 48" aria-hidden="true" focusable="false">' +
      '<path d="M19.2 14.1a5 5 0 1 1 8.8 3.2c-1 1.2-2.3 1.8-3.8 2.7l15 9.2a3.4 3.4 0 0 1-1.8 6.3H10.6a3.4 3.4 0 0 1-1.8-6.3L24 20"/>' +
      '</svg>';
  }

  function Prompt(root, options) {
    this.root = root;
    this.options = {};
    this.editing = false;
    this.saving = false;
    this.error = '';
    this.shown = false;
    this.update(options || {});
  }

  Prompt.prototype.key = function () {
    return String(this.options.context || 'browse') + ':' +
      String(this.options.categoryKey || 'unknown') + ':size';
  };

  Prompt.prototype.wasDismissed = function () {
    var stored = readStore(window.localStorage, STORAGE_KEY);
    var dismissedAt = Number(stored.dismissed && stored.dismissed[this.key()] || 0);
    return dismissedAt > 0 && Date.now() - dismissedAt < COOLDOWN_MS;
  };

  Prompt.prototype.wasShownThisSession = function () {
    var session = readStore(window.sessionStorage, SESSION_KEY);
    return !!(session.shown && session.shown[this.key()]);
  };

  Prompt.prototype.rememberShown = function () {
    var session = readStore(window.sessionStorage, SESSION_KEY);
    session.shown = session.shown || {};
    session.shown[this.key()] = Date.now();
    writeStore(window.sessionStorage, SESSION_KEY, session);
  };

  Prompt.prototype.rememberDismissed = function () {
    var stored = readStore(window.localStorage, STORAGE_KEY);
    stored.version = 1;
    stored.dismissed = stored.dismissed || {};
    stored.dismissed[this.key()] = Date.now();
    writeStore(window.localStorage, STORAGE_KEY, stored);
  };

  Prompt.prototype.update = function (options) {
    options = options || {};
    var previousKey = this.key();
    var self = this;
    Object.keys(options).forEach(function (key) {
      self.options[key] = options[key];
    });
    if (previousKey !== this.key()) {
      this.editing = false;
      this.error = '';
      this.shown = false;
    }
    this.render();
    return this;
  };

  Prompt.prototype.shouldRender = function () {
    var choices = Array.isArray(this.options.choices) ? this.options.choices : [];
    if (!this.root || !this.options.categoryKey) return false;
    if (this.options.loading === true) return !this.wasDismissed();
    if (selectedValues(this.options.selected).length) return true;
    if (choices.length < 2) return false;
    if (this.wasDismissed()) return false;
    return this.shown || !this.wasShownThisSession();
  };

  Prompt.prototype.renderLoading = function () {
    var category = String(this.options.categoryLabel || 'this collection');
    this.root.innerHTML =
      '<section class="pw-pref-prompt pw-pref-prompt--loading" role="status" aria-live="polite">' +
        '<button type="button" class="pw-pref-prompt__dismiss" data-pw-pref-dismiss aria-label="Dismiss this question">×</button>' +
        '<header class="pw-pref-prompt__head">' +
          '<span class="pw-pref-prompt__emblem">' + hangerIcon() + '</span>' +
          '<span><small>Your Preworn</small><h2>Let us build your style.</h2>' +
          '<p>Getting the available ' + escapeHtml(category.toLowerCase()) + ' sizes ready…</p></span>' +
        '</header>' +
        '<span class="pw-pref-prompt__loading-bar" aria-hidden="true"></span>' +
      '</section>';
  };

  Prompt.prototype.renderCollapsed = function () {
    var selected = selectedValues(this.options.selected);
    var category = String(this.options.categoryLabel || 'this collection');
    var wardrobeActive = this.options.wardrobeActive !== false;
    var count = formatCount(this.options.matchingCount);
    var countCopy = !wardrobeActive
      ? 'Wardrobe is off — your preferences are saved'
      : (count
        ? count + ' ' + (Number(this.options.matchingCount) === 1 ? 'item' : 'items') + ' in your size'
        : (this.saving ? 'Saving this preference…' : 'Ready whenever you shop this collection'));

    this.root.innerHTML =
      '<section class="pw-pref-prompt pw-pref-prompt--saved" aria-label="Saved size preference">' +
        '<span class="pw-pref-prompt__emblem">' + hangerIcon() + '</span>' +
        '<span class="pw-pref-prompt__saved-copy">' +
          '<small>Your Preworn</small>' +
          '<strong>Your ' + escapeHtml(category) + (selected.length === 1 ? ' size is ' : ' sizes are ') +
            escapeHtml(selected.join(', ')) + '</strong>' +
          '<span>' + escapeHtml(countCopy) + '</span>' +
        '</span>' +
        '<span class="pw-pref-prompt__saved-actions">' +
          '<button type="button" class="pw-pref-prompt__wardrobe-toggle' +
            (wardrobeActive ? ' is-active' : '') + '" data-pw-pref-wardrobe-toggle ' +
            'aria-pressed="' + (wardrobeActive ? 'true' : 'false') + '" ' +
            'aria-label="Turn Wardrobe ' + (wardrobeActive ? 'off' : 'on') + '"' +
            (this.saving ? ' disabled' : '') + '>' +
            '<span>Wardrobe</span>' +
            '<i aria-hidden="true"><b></b></i>' +
            '<strong>' + (wardrobeActive ? 'On' : 'Off') + '</strong>' +
          '</button>' +
          '<button type="button" class="pw-pref-prompt__edit" data-pw-pref-edit>Edit</button>' +
        '</span>' +
      '</section>';
  };

  Prompt.prototype.renderExpanded = function () {
    var self = this;
    var choices = Array.isArray(this.options.choices) ? this.options.choices : [];
    var selected = selectedValues(this.options.selected);
    var colourChoices = Array.isArray(this.options.colourChoices)
      ? this.options.colourChoices : [];
    var selectedColours = selectedValues(this.options.selectedColours);
    var patternChoices = Array.isArray(this.options.patternChoices)
      ? this.options.patternChoices : [];
    var selectedPatterns = selectedValues(this.options.selectedPatterns);
    var category = String(this.options.categoryLabel || 'this collection');
    var imageUrl = String(this.options.imageUrl || '');
    var chips = choices.map(function (choice) {
      var value = String(choice.value || choice.label || '');
      var active = selected.indexOf(value) !== -1;
      return '<button type="button" class="pw-pref-prompt__size' +
        (active ? ' is-selected' : '') + '" data-pw-pref-size="' +
        escapeHtml(value) + '" aria-pressed="' + (active ? 'true' : 'false') + '"' +
        (self.saving ? ' disabled' : '') + '>' + escapeHtml(choice.label || value) + '</button>';
    }).join('');
    var visual = imageUrl
      ? '<span class="pw-pref-prompt__visual"><img src="' + escapeHtml(imageUrl) +
        '" alt="" loading="lazy"></span>'
      : '<span class="pw-pref-prompt__visual pw-pref-prompt__visual--icon">' + hangerIcon() + '</span>';
    var done = selected.length
      ? '<button type="button" class="pw-pref-prompt__done" data-pw-pref-done>' +
          'Show me ' + escapeHtml(category.toLowerCase()) + ' in my ' +
          (selected.length === 1 ? 'size' : 'sizes') + ' <span aria-hidden="true">→</span></button>'
      : '';
    var colourChips = colourChoices.map(function (choice) {
      var value = String(choice.value || choice.label || '');
      var active = selectedColours.indexOf(value) !== -1;
      return '<button type="button" class="pw-pref-prompt__taste pw-pref-prompt__taste--colour' +
        (active ? ' is-selected' : '') + '" data-pw-pref-colour="' + escapeHtml(value) +
        '" aria-pressed="' + (active ? 'true' : 'false') + '"' +
        (self.saving ? ' disabled' : '') + '><i style="' +
        escapeHtml(choice.swatch || '') + '" aria-hidden="true"></i><span>' +
        escapeHtml(choice.label || value) + '</span></button>';
    }).join('');
    var patternChips = patternChoices.map(function (choice) {
      var value = String(choice.value || choice.label || '');
      var active = selectedPatterns.indexOf(value) !== -1;
      return '<button type="button" class="pw-pref-prompt__taste' +
        (active ? ' is-selected' : '') + '" data-pw-pref-pattern="' + escapeHtml(value) +
        '" aria-pressed="' + (active ? 'true' : 'false') + '"' +
        (self.saving ? ' disabled' : '') + '>' + escapeHtml(choice.label || value) + '</button>';
    }).join('');
    var tastes = selected.length && (colourChips || patternChips)
      ? '<section class="pw-pref-prompt__tastes" aria-labelledby="PrewornPreferenceTasteTitle">' +
          '<header><span><small>OPTIONAL NEXT STEP</small>' +
          '<strong id="PrewornPreferenceTasteTitle">Make it more you.</strong></span>' +
          '<p>Choose what you usually like. We’ll improve recommendations without hiding everything else.</p></header>' +
          (colourChips ? '<div class="pw-pref-prompt__taste-group"><b>Colours you like</b>' +
            '<div role="group" aria-label="Choose colours you like">' + colourChips + '</div></div>' : '') +
          (patternChips ? '<div class="pw-pref-prompt__taste-group"><b>Patterns you like</b>' +
            '<div role="group" aria-label="Choose patterns you like">' + patternChips + '</div></div>' : '') +
        '</section>'
      : '';

    this.root.innerHTML =
      '<section class="pw-pref-prompt" role="region" aria-labelledby="PrewornPreferenceTitle">' +
        '<button type="button" class="pw-pref-prompt__dismiss" data-pw-pref-dismiss aria-label="Dismiss this question">×</button>' +
        '<header class="pw-pref-prompt__head">' +
          '<span class="pw-pref-prompt__emblem">' + hangerIcon() + '</span>' +
          '<span><small>Your Preworn</small><h2 id="PrewornPreferenceTitle">Let us build your style.</h2>' +
          '<p>Tell us one size and we’ll show you pieces that fit — now and every time you shop.</p></span>' +
        '</header>' +
        '<div class="pw-pref-prompt__question">' + visual +
          '<div class="pw-pref-prompt__answer">' +
            '<strong>' + escapeHtml(category) + '</strong>' +
            '<p>What size do you usually wear?</p>' +
            '<div class="pw-pref-prompt__sizes" role="group" aria-label="Choose your ' +
              escapeHtml(category) + ' size">' + chips + '</div>' +
            '<span class="pw-pref-prompt__remember"><i aria-hidden="true">✓</i>' +
              escapeHtml(this.options.storageLabel || 'Remember this for next time') + '</span>' +
            '<span class="pw-pref-prompt__status' + (this.error ? ' is-error' : '') +
              '" role="status" aria-live="polite">' +
              escapeHtml(this.error || (this.saving ? 'Finding pieces in your size…' : '')) + '</span>' +
          '</div>' +
        '</div>' +
        tastes +
        done +
        '<button type="button" class="pw-pref-prompt__build" data-pw-pref-build>' +
          'Build my style <span>(it only takes a minute)</span></button>' +
        '<footer class="pw-pref-prompt__benefits" aria-label="Benefits of personalising Preworn">' +
          '<span>' + hangerIcon() + 'Better recommendations</span>' +
          '<span aria-hidden="true">♡</span><span>Find your favourite brands</span>' +
          '<span aria-hidden="true">✦</span><span>A more personal Preworn</span>' +
        '</footer>' +
      '</section>';
  };

  Prompt.prototype.bind = function () {
    var self = this;
    var dismiss = this.root.querySelector('[data-pw-pref-dismiss]');
    if (dismiss) dismiss.addEventListener('click', function () {
      self.rememberDismissed();
      self.root.hidden = true;
      emit('style_prompt_dismissed', {
        context: self.options.context,
        category: self.options.categoryKey,
        preference: 'size'
      });
      if (typeof self.options.onDismiss === 'function') self.options.onDismiss();
    });

    var edit = this.root.querySelector('[data-pw-pref-edit]');
    if (edit) edit.addEventListener('click', function () {
      self.editing = true;
      self.error = '';
      self.render();
    });

    var wardrobeToggle = this.root.querySelector('[data-pw-pref-wardrobe-toggle]');
    if (wardrobeToggle) wardrobeToggle.addEventListener('click', function () {
      if (self.saving) return;
      var previousActive = self.options.wardrobeActive !== false;
      var nextActive = !previousActive;
      self.options.wardrobeActive = nextActive;
      self.saving = true;
      self.error = '';
      self.render();
      emit(nextActive ? 'wardrobe_activated_from_collection' : 'wardrobe_deactivated_from_collection', {
        context: self.options.context,
        category: self.options.categoryKey
      });
      var request = typeof self.options.onWardrobeToggle === 'function'
        ? self.options.onWardrobeToggle(nextActive) : Promise.resolve(nextActive);
      Promise.resolve(request).then(function () {
        self.saving = false;
        self.render();
      }).catch(function (error) {
        self.saving = false;
        self.options.wardrobeActive = previousActive;
        self.error = error && error.message
          ? error.message : 'We could not change Wardrobe mode. Please try again.';
        self.render();
      });
    });

    var done = this.root.querySelector('[data-pw-pref-done]');
    if (done) done.addEventListener('click', function () {
      self.editing = false;
      self.error = '';
      self.render();
      if (typeof self.options.onDone === 'function') self.options.onDone();
    });

    var build = this.root.querySelector('[data-pw-pref-build]');
    if (build) build.addEventListener('click', function () {
      emit('wardrobe_builder_started_from_prompt', {
        context: self.options.context,
        category: self.options.categoryKey
      });
      if (typeof self.options.onBuild === 'function') self.options.onBuild();
    });

    var sizes = this.root.querySelectorAll('[data-pw-pref-size]');
    for (var index = 0; index < sizes.length; index++) {
      sizes[index].addEventListener('click', function () {
        var value = this.getAttribute('data-pw-pref-size') || '';
        var previousSelected = selectedValues(self.options.selected);
        var nextSelected = previousSelected.slice();
        var selectedIndex = nextSelected.indexOf(value);
        if (selectedIndex === -1) nextSelected.push(value);
        else if (nextSelected.length > 1) nextSelected.splice(selectedIndex, 1);
        else {
          self.error = 'Choose at least one size.';
          self.render();
          return;
        }
        var changed = previousSelected.length > 0;
        self.options.selected = nextSelected;
        self.saving = true;
        self.editing = true;
        self.error = '';
        self.render();
        emit(changed ? 'style_preference_changed' : 'style_preference_selected', {
          context: self.options.context,
          category: self.options.categoryKey,
          preference: 'size',
          sizeType: self.options.sizeType
        });
        var request = typeof self.options.onSelect === 'function'
          ? self.options.onSelect(nextSelected) : Promise.resolve();
        Promise.resolve(request).then(function () {
          self.saving = false;
          self.error = '';
          self.render();
        }).catch(function (error) {
          self.saving = false;
          self.editing = true;
          self.options.selected = previousSelected;
          self.error = error && error.message
            ? error.message : 'We could not save that size. Please try again.';
          self.render();
        });
      });
    }

    function bindTasteChoices(selector, attribute, selectedKey, callbackKey, preference) {
      var buttons = self.root.querySelectorAll(selector);
      for (var choiceIndex = 0; choiceIndex < buttons.length; choiceIndex++) {
        buttons[choiceIndex].addEventListener('click', function () {
          var value = this.getAttribute(attribute) || '';
          var previousSelected = selectedValues(self.options[selectedKey]);
          var nextSelected = previousSelected.slice();
          var selectedIndex = nextSelected.indexOf(value);
          if (selectedIndex === -1) nextSelected.push(value);
          else nextSelected.splice(selectedIndex, 1);
          self.options[selectedKey] = nextSelected;
          self.saving = true;
          self.editing = true;
          self.error = '';
          self.render();
          emit(previousSelected.length ? 'style_preference_changed' : 'style_preference_selected', {
            context: self.options.context,
            category: self.options.categoryKey,
            preference: preference,
            selectedCount: nextSelected.length
          });
          var request = typeof self.options[callbackKey] === 'function'
            ? self.options[callbackKey](nextSelected) : Promise.resolve();
          Promise.resolve(request).then(function () {
            self.saving = false;
            self.error = '';
            self.render();
          }).catch(function (error) {
            self.saving = false;
            self.editing = true;
            self.options[selectedKey] = previousSelected;
            self.error = error && error.message
              ? error.message : 'We could not save that preference. Please try again.';
            self.render();
          });
        });
      }
    }

    bindTasteChoices(
      '[data-pw-pref-colour]',
      'data-pw-pref-colour',
      'selectedColours',
      'onColoursChange',
      'colour'
    );
    bindTasteChoices(
      '[data-pw-pref-pattern]',
      'data-pw-pref-pattern',
      'selectedPatterns',
      'onPatternsChange',
      'pattern'
    );
  };

  Prompt.prototype.render = function () {
    if (!this.shouldRender()) {
      if (this.root) this.root.hidden = true;
      return;
    }
    this.root.hidden = false;
    if (this.options.loading === true) this.renderLoading();
    else if (selectedValues(this.options.selected).length && !this.editing) this.renderCollapsed();
    else this.renderExpanded();
    this.bind();

    if (!this.shown && this.options.loading !== true && !this.options.selected) {
      this.shown = true;
      this.rememberShown();
      emit('style_prompt_shown', {
        context: this.options.context,
        category: this.options.categoryKey,
        preference: 'size',
        optionCount: (this.options.choices || []).length
      });
    }
  };

  window.PrewornPreferencePrompt = {
    mount: function (root, options) {
      return root ? new Prompt(root, options || {}) : null;
    },
    cooldownMs: COOLDOWN_MS
  };
})();
